<template>
	<div id="Supplies">
		<div class="wrapper">
			<!-- 배너 -->
			<section class="banner">
				<div class="banner-text">
					<span class="banner-label">LAB SUPPLIES &amp; INSTRUMENTS</span>
					<h2>실험 소모품 · 용품 · 기기</h2>
					<p>시약을 제외한 연구실 필수 용품을 카테고리별로 한눈에 확인하세요.</p>
				</div>
			</section>

			<!-- 브랜드 하나만 골랐으면 화학물질 / 실험용품·기기 탭 -->
			<brand-tabs v-if="brandIds.length === 1" class="brand-tabs-wrap" :brandId="brandIds[0]" active="supplies" />

			<div class="body">
				<!-- 좌측 카테고리 트리 -->
				<aside class="category-tree" :class="{ open: showMobileTree }">
					<button class="tree-toggle" @click="showMobileTree = !showMobileTree">
						카테고리 <span>{{ showMobileTree ? '접기 ▲' : '펼치기 ▼' }}</span>
					</button>
					<div class="tree-body">
						<h4>카테고리</h4>
						<ul>
							<li class="tree-all">
								<button :class="{ active: !parentIdx }" @click="selectParent(null)">
									<span class="name">전체</span>
									<em>{{ totalCount }}</em>
								</button>
							</li>
							<li v-for="parent in categories" :key="parent.idx" class="tree-parent">
								<button :class="{ active: parentIdx === parent.idx && !childIdx, current: parentIdx === parent.idx }" @click="selectParent(parent)">
									<span class="arrow">{{ isExpanded(parent) ? '▼' : '▶' }}</span>
									<span class="name">{{ parent.name }}</span>
									<em>{{ parent.count }}</em>
								</button>
								<ul v-show="isExpanded(parent)" class="tree-children">
									<li v-for="child in parent.children" :key="child.idx">
										<button :class="{ active: childIdx === child.idx }" @click="selectChild(parent, child)">
											<span class="name">{{ child.name }}</span>
											<em>{{ child.count }}</em>
										</button>
									</li>
								</ul>
							</li>
						</ul>
						<!-- 브랜드 필터: 현재 카테고리/검색어에 있는 브랜드만, 많은 순 -->
						<div class="brand-filter" v-if="brands.length > 0">
							<h4>브랜드</h4>
							<div class="brand-search">
								<input v-model.trim="brandKeyword" type="text" placeholder="브랜드 검색" />
							</div>
							<ul class="brand-list">
								<li v-for="brand in visibleBrands" :key="brand.id">
									<label>
										<input type="checkbox" :value="brand.id" v-model="brandIds" @change="onChangeBrand" />
										<span class="name">{{ brand.name }}</span>
										<em>{{ brand.count }}</em>
									</label>
								</li>
								<li v-if="visibleBrands.length === 0" class="empty">일치하는 브랜드가 없어요</li>
							</ul>
							<button v-if="canToggleMoreBrands" class="more" @click="showAllBrands = !showAllBrands">
								{{ showAllBrands ? '접기 ▲' : `더보기 (${filteredBrands.length - BRAND_LIMIT}) ▼` }}
							</button>
						</div>
					</div>
				</aside>

				<!-- 우측 목록 -->
				<main class="content">
					<div class="toolbar">
						<div class="toolbar-left">
							<p class="breadcrumb">
								<a @click.prevent="selectParent(null)">전체</a>
								<template v-if="selectedParent">
									<span class="sep">›</span>
									<a @click.prevent="selectParent(selectedParent, true)">{{ selectedParent.name }}</a>
								</template>
								<template v-if="selectedChild">
									<span class="sep">›</span>
									<b>{{ selectedChild.name }}</b>
								</template>
							</p>
							<p class="result-count">
								총 <b>{{ CommaNum(count) }}</b
								>개
							</p>
						</div>
						<div class="toolbar-right">
							<form class="search-box" @submit.prevent="search">
								<input v-model.trim="keywordInput" type="text" placeholder="상품명 · 브랜드 검색" />
								<button type="submit">검색</button>
							</form>
							<select v-model="order" @change="onChangeOrder">
								<option v-for="opt in orderOptions" :key="opt.value" :value="opt.value">{{ opt.name }}</option>
							</select>
						</div>
					</div>

					<div class="selected-filters" v-if="selectedBrands.length > 0">
						<span class="chip" v-for="brand in selectedBrands" :key="brand.id">
							{{ brand.name }}
							<button @click="removeBrand(brand.id)" aria-label="브랜드 해제">✕</button>
						</span>
						<button class="clear" @click="clearBrands">전체 해제</button>
					</div>

					<loading v-if="isLoading" />
					<div v-else-if="list.length > 0" class="card-grid">
						<supplies-card v-for="item in list" :key="item.catalogId" :item="item" />
					</div>
					<div v-else class="empty">
						<p>조건에 맞는 상품이 없습니다.</p>
					</div>

					<common-pagenation
						v-show="count > 0"
						:key="pagerKey"
						class="pager"
						:total="count"
						:isPerPage="false"
						:defaultLength="length"
						@page="onPage"
					/>
				</main>
			</div>
		</div>
	</div>
</template>

<script>
import SuppliesCard from '@/views/supplies/components/SuppliesCard.vue'
import BrandTabs from '@/components/search/BrandTabs.vue'
import CommonPagenation from '@/components/pagenation/CommonPagenation.vue'
import Loading from '@/components/Loading.vue'
import suppliesAPI from '@/services/api/supplies'
import mixin from '@/mixins'
import cookies from 'vue-cookies'

export default {
	name: 'Supplies',
	mixins: [mixin],
	components: { SuppliesCard, CommonPagenation, Loading, BrandTabs },
	data() {
		return {
			categories: [],
			parentIdx: null,
			childIdx: null,
			expanded: {}, // 펼쳐진 대분류 { [idx]: true }
			showMobileTree: false,
			keyword: '',
			keywordInput: '',
			// 브랜드 필터
			brands: [], // [{ id, name, count }] 현재 조건에 해당하는 브랜드
			brandIds: [], // 선택한 브랜드 id
			brandKeyword: '', // 브랜드 목록 안에서 찾기
			showAllBrands: false,
			BRAND_LIMIT: 8,
			brandSeq: 0,
			order: 'NAME',
			orderOptions: [
				{ value: 'NAME', name: '이름순' },
				{ value: 'PRICE_ASC', name: '낮은 가격순' },
				{ value: 'PRICE_DESC', name: '높은 가격순' },
				{ value: 'DISCOUNT', name: '할인율순' },
			],
			offset: 0,
			length: 20,
			count: 0,
			list: [],
			isLoading: true,
			pagerKey: 0,
			requestSeq: 0,
		}
	},
	computed: {
		selectedParent() {
			return this.categories.find(v => v.idx === this.parentIdx) || null
		},
		selectedChild() {
			if (!this.selectedParent) return null
			return this.selectedParent.children.find(v => v.idx === this.childIdx) || null
		},
		// 브랜드 목록: 선택한 브랜드를 위로, 나머지는 건수 많은 순. 검색어가 있으면 이름으로 걸러낸다.
		filteredBrands() {
			const kw = this.brandKeyword.toLowerCase()
			const list = kw ? this.brands.filter(b => b.name.toLowerCase().includes(kw)) : this.brands
			const selected = list.filter(b => this.brandIds.includes(b.id))
			const rest = list.filter(b => !this.brandIds.includes(b.id))
			return [...selected, ...rest]
		},
		visibleBrands() {
			if (this.showAllBrands || this.brandKeyword) return this.filteredBrands
			return this.filteredBrands.slice(0, this.BRAND_LIMIT)
		},
		canToggleMoreBrands() {
			return !this.brandKeyword && this.filteredBrands.length > this.BRAND_LIMIT
		},
		selectedBrands() {
			return this.brandIds.map(id => this.brands.find(b => b.id === id) || { id, name: `브랜드 ${id}` })
		},
		totalCount() {
			return this.categories.reduce((sum, v) => sum + v.count, 0)
		},
	},
	async created() {
		const { p, c, q, o } = this.$route.query
		this.parentIdx = p ? Number(p) : null
		this.childIdx = c ? Number(c) : null
		this.keyword = q || ''
		this.keywordInput = this.keyword
		if (this.orderOptions.some(v => v.value === o)) this.order = o
		if (this.parentIdx) this.$set(this.expanded, this.parentIdx, true)
		if (this.$route.query.b) {
			this.brandIds = String(this.$route.query.b)
				.split(',')
				.map(Number)
				.filter(Boolean)
		}

		await Promise.all([this.loadCategories(), this.loadBrands(), this.loadList()])
	},
	methods: {
		async loadCategories() {
			try {
				const { data } = await suppliesAPI.lookupCategories()
				this.categories = data
			} catch (e) {
				this.categories = []
			}
		},
		// 현재 카테고리/검색어에 해당하는 브랜드 목록 (브랜드 필터 자신은 조건에서 제외)
		async loadBrands() {
			const seq = ++this.brandSeq
			try {
				const { data } = await suppliesAPI.lookupBrands({
					parentIdx: this.parentIdx || undefined,
					childIdx: this.childIdx || undefined,
					keyword: this.keyword || undefined,
					brandIds: this.brandIds.length ? this.brandIds : undefined,
				})
				if (seq === this.brandSeq) this.brands = data
			} catch (e) {
				if (seq === this.brandSeq) this.brands = []
			}
		},
		onChangeBrand() {
			// 브랜드 목록은 브랜드 선택과 무관하므로 다시 받지 않는다.
			this.resetAndLoad(false)
		},
		removeBrand(id) {
			this.brandIds = this.brandIds.filter(v => v !== id)
			this.resetAndLoad(false)
		},
		clearBrands() {
			this.brandIds = []
			this.resetAndLoad(false)
		},
		async loadList() {
			const seq = ++this.requestSeq
			this.isLoading = true
			try {
				const params = {
					parentIdx: this.parentIdx || undefined,
					childIdx: this.childIdx || undefined,
					keyword: this.keyword || undefined,
					brandIds: this.brandIds.length ? this.brandIds : undefined,
					order: this.order,
					offset: this.offset,
					length: this.length,
					instituteId: cookies.get('currentInstitute2')?.id || undefined,
				}
				const { data } = await suppliesAPI.lookupList(params)
				if (seq !== this.requestSeq) return
				this.count = data.count
				this.list = data.list
			} catch (e) {
				if (seq !== this.requestSeq) return
				this.count = 0
				this.list = []
			} finally {
				if (seq === this.requestSeq) this.isLoading = false
			}
		},
		// 필터가 바뀌면 첫 페이지부터 다시 조회하고 페이저를 초기화한다.
		async resetAndLoad(reloadBrands = true) {
			this.offset = 0
			this.pagerKey += 1
			this.syncQuery()
			await Promise.all([this.loadList(), reloadBrands ? this.loadBrands() : null])
		},
		syncQuery() {
			const query = {}
			if (this.parentIdx) query.p = String(this.parentIdx)
			if (this.childIdx) query.c = String(this.childIdx)
			if (this.keyword) query.q = this.keyword
			if (this.brandIds.length) query.b = this.brandIds.join(',')
			if (this.order !== 'NAME') query.o = this.order
			this.$router.replace({ query }).catch(() => {})
		},
		isExpanded(parent) {
			return !!this.expanded[parent.idx]
		},
		// 대분류 클릭: 선택 + 펼침 토글 (이미 선택된 대분류를 다시 누르면 접기). keepOpen이면 접지 않는다.
		selectParent(parent, keepOpen = false) {
			if (!parent) {
				this.parentIdx = null
				this.childIdx = null
			} else {
				const isSame = this.parentIdx === parent.idx && !this.childIdx
				this.$set(this.expanded, parent.idx, keepOpen ? true : isSame ? !this.expanded[parent.idx] : true)
				this.parentIdx = parent.idx
				this.childIdx = null
			}
			this.resetAndLoad()
		},
		selectChild(parent, child) {
			this.parentIdx = parent.idx
			this.childIdx = child.idx
			this.$set(this.expanded, parent.idx, true)
			this.showMobileTree = false
			this.resetAndLoad()
		},
		search() {
			this.keyword = this.keywordInput
			this.resetAndLoad()
		},
		onChangeOrder() {
			this.resetAndLoad()
		},
		onPage({ offset }) {
			if (offset === this.offset) return
			this.offset = offset
			this.loadList()
			window.scrollTo({ top: 0, behavior: 'smooth' })
		},
	},
}
</script>

<style lang="scss" scoped>
#Supplies {
	background: #fff;
	.wrapper {
		max-width: 1280px;
		margin: 0 auto;
		padding: 24px 16px 60px;
	}
	.banner {
		display: flex;
		align-items: center;
		min-height: 160px;
		padding: 32px 40px;
		border-radius: 12px;
		background: linear-gradient(120deg, #1d4f80 0%, #3d86c6 100%);
		color: #fff;
		.banner-label {
			font-size: 0.75rem;
			font-weight: 700;
			letter-spacing: 0.12em;
			opacity: 0.8;
		}
		h2 {
			margin: 6px 0 8px;
			font-size: 1.6rem;
			font-weight: 700;
		}
		p {
			margin: 0;
			opacity: 0.9;
		}
	}
	.brand-tabs-wrap {
		margin: 16px 0 0;
	}
	.body {
		display: flex;
		align-items: flex-start;
		gap: 28px;
		margin-top: 24px;
	}

	/* 좌측 트리 */
	.category-tree {
		flex: 0 0 240px;
		position: sticky;
		top: 16px;
		max-height: calc(100vh - 32px);
		overflow-y: auto;
		border: 1px solid #e5e9ee;
		border-radius: 10px;
		background: #fff;
		.tree-toggle {
			display: none;
		}
		h4 {
			margin: 0;
			padding: 14px 16px;
			font-size: 0.95rem;
			font-weight: 700;
			border-bottom: 1px solid #eef1f4;
		}
		ul {
			margin: 0;
			padding: 0;
			list-style: none;
		}
		button {
			display: flex;
			align-items: center;
			width: 100%;
			padding: 10px 16px;
			border: none;
			background: transparent;
			text-align: left;
			font-size: 0.9rem;
			color: #394755;
			cursor: pointer;
			.arrow {
				flex: 0 0 18px;
				font-size: 0.6rem;
				color: #98a2ad;
			}
			.name {
				flex: 1;
			}
			em {
				font-style: normal;
				font-size: 0.75rem;
				color: #8a94a0;
			}
			&:hover {
				background: #f3f7fb;
			}
		}
		.tree-parent > button.current {
			font-weight: 700;
			color: #1d4f80;
		}
		.tree-children button {
			padding: 7px 16px 7px 34px;
			font-size: 0.84rem;
			color: #4a5764;
		}
		button.active {
			background: #eaf3fb;
			color: #1d4f80;
			font-weight: 700;
			box-shadow: inset 3px 0 0 #1d4f80;
			em {
				color: #1d4f80;
			}
		}
	}

	/* 좌측 브랜드 필터 */
	.category-tree .brand-filter {
		border-top: 1px solid #eef1f4;
		h4 {
			border-bottom: 0;
			padding-bottom: 8px;
		}
		.brand-search {
			padding: 0 16px 8px;
			input {
				width: 100%;
				padding: 7px 10px;
				border: 1px solid #d6dde4;
				border-radius: 6px;
				font-size: 0.85rem;
				&:focus {
					outline: none;
					border-color: #3d86c6;
				}
			}
		}
		.brand-list {
			li label {
				display: flex;
				align-items: center;
				gap: 8px;
				margin: 0;
				padding: 6px 16px;
				font-size: 0.85rem;
				color: #394755;
				cursor: pointer;
				&:hover {
					background: #f3f7fb;
				}
				input {
					flex: 0 0 auto;
					margin: 0;
					accent-color: #1d4f80;
				}
				.name {
					flex: 1;
					min-width: 0;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}
				em {
					font-style: normal;
					font-size: 0.75rem;
					color: #8a94a0;
				}
			}
			.empty {
				padding: 8px 16px;
				font-size: 0.8rem;
				color: #98a2ad;
			}
		}
		button.more {
			justify-content: center;
			padding: 9px 16px;
			font-size: 0.8rem;
			color: #1d4f80;
			font-weight: 600;
		}
	}

	/* 선택한 브랜드 칩 */
	.selected-filters {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		margin-bottom: 14px;
		.chip {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			padding: 5px 6px 5px 12px;
			border-radius: 16px;
			background: #eaf3fb;
			color: #1d4f80;
			font-size: 0.85rem;
			font-weight: 700;
			button {
				width: 20px;
				height: 20px;
				border: none;
				border-radius: 50%;
				background: transparent;
				color: #1d4f80;
				font-size: 0.7rem;
				cursor: pointer;
				&:hover {
					background: #cfe3f5;
				}
			}
		}
		.clear {
			border: none;
			background: none;
			color: #6b7785;
			font-size: 0.8rem;
			text-decoration: underline;
			cursor: pointer;
		}
	}

	/* 우측 콘텐츠 */
	.content {
		flex: 1;
		min-width: 0;
	}
	.toolbar {
		display: flex;
		align-items: flex-end;
		justify-content: space-between;
		gap: 12px;
		margin-bottom: 16px;
		.breadcrumb {
			margin: 0 0 4px;
			font-size: 0.85rem;
			color: #6b7785;
			a {
				cursor: pointer;
				&:hover {
					color: #1d4f80;
					text-decoration: underline;
				}
			}
			.sep {
				margin: 0 6px;
				color: #b4bcc5;
			}
			b {
				color: #1f2933;
			}
		}
		.result-count {
			margin: 0;
			color: #4a5764;
		}
		.toolbar-right {
			display: flex;
			gap: 8px;
			.search-box {
				display: flex;
				input {
					width: 220px;
					padding: 6px 10px;
					border: 1px solid #d6dde4;
					border-right: none;
					border-radius: 6px 0 0 6px;
				}
				button {
					padding: 6px 14px;
					border: 1px solid #1d4f80;
					border-radius: 0 6px 6px 0;
					background: #1d4f80;
					color: #fff;
					cursor: pointer;
				}
			}
			select {
				padding: 6px 10px;
				border: 1px solid #d6dde4;
				border-radius: 6px;
				background: #fff;
			}
		}
	}
	.card-grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 16px;
	}
	.empty {
		padding: 80px 0;
		text-align: center;
		color: #8a94a0;
	}
	.pager {
		margin-top: 32px;
	}
}

@media (max-width: 1100px) {
	#Supplies .card-grid {
		grid-template-columns: repeat(3, minmax(0, 1fr));
	}
}
@media (max-width: 860px) {
	#Supplies {
		.body {
			flex-direction: column;
			gap: 16px;
		}
		.category-tree {
			position: static;
			flex: none;
			width: 100%;
			max-height: none;
			.tree-toggle {
				display: flex;
				justify-content: space-between;
				align-items: center;
				padding: 12px 16px;
				font-weight: 700;
				span {
					font-size: 0.8rem;
					font-weight: 400;
					color: #6b7785;
				}
			}
			.tree-body {
				display: none;
				max-height: 50vh;
				overflow-y: auto;
				border-top: 1px solid #eef1f4;
				h4 {
					display: none;
				}
			}
			&.open .tree-body {
				display: block;
			}
		}
		.card-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.toolbar {
			flex-direction: column;
			align-items: stretch;
		}
		.banner {
			padding: 24px;
		}
	}
}
</style>
